import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { isArray } from 'lodash';
import {Grid, GridColumn as Column, GridToolbar} from '@progress/kendo-react-grid';
import { process as kendoProcess } from '@progress/kendo-data-query';
import {Operators, TextFilter, NumericFilter} from '@progress/kendo-react-data-tools';
import FilterPanel from '../K2AFilterControl/FilterPanel';

import BobLoadingMask from '../BobLoadingMask';

import * as BobActions from '../../actions/bobActions';
import * as DashboardActions from '../../actions/dashboardActions';
import { kendoGridInlineStyling } from '../../helpers/kendoGridInlineStyleHelper';
import * as classes from './EquipmentScoreGrid.module.css';

const initialDataStateFilter = {
    logic:   'and',
    filters: []
};

const initialDataState = {
    sort:   [],
    filter: initialDataStateFilter
};

const equipmentGridColumns = [
    { name: 'name', label: 'Equipment', filter: TextFilter, operators: Operators.text },
    { name: 'score', label: 'Score', filter: NumericFilter, operators: Operators.numeric }
];

const EquipmentScoreGrid = ({
    activeNavClient,
    activeNavCompany,
    activeNavSite,
    bobLoading,
    dashboardEndTimeStamp,
    dashboardStartTimeStamp,
    desiredInterval,
    fetchEntitySensors,
    fetchRuleScoreKpis,
    fetchScoringData,
    fetchSensorIds,
    fetchSiteEquipmentScores,
    navEquipment,
    refreshEquipmentGrid,
    ruleScoreKpis,
    selectedEquipment,
    selectedEquipmentTypes,
    selectedEquipmentRule,
    selectedSystems,
    setEntityScoringData,
    setRuleScoreKpis,
    setSelectedEquipment,
    setSelectedEquipmentRule,
    setSelectedRuleSensors,
    setSelectedSensors,
    setSelectedWeatherSensors,
    view,
    workbookModeEnabled,
    ...props
}) => {
    const [filteredEquipment, setFilteredEquipment] = useState([]);
    const [gridDataState, setGridDataState] = useState(initialDataState);
    const [gridFilterText, setGridFilterText] = useState('');
    const [gridFields] = useState(equipmentGridColumns);

    useEffect(() => {
        const init = async() => {
            const equipmentTypeIds = selectedEquipmentTypes.map(item => item.id);
            const systemIds = selectedSystems.map(item => item.id);

            let promises = [];
            promises.push(fetchSiteEquipmentScores(activeNavSite.id, systemIds, equipmentTypeIds, { startDate: dashboardStartTimeStamp, endDate: dashboardEndTimeStamp }));
            if (selectedEquipment && selectedEquipment.id) {
                promises.push(fetchRuleScoreKpis(activeNavCompany.id, selectedEquipment.id, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Equipment' }));
            }
            await Promise.all(promises).then((results) => {
                setFilteredEquipment(results[0]);
            }).catch(err => err);
        };

        if (refreshEquipmentGrid) init();
    }, [refreshEquipmentGrid]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setGridDataState({...gridDataState, filter: initialDataStateFilter});
            setGridFilterText('');
        }
    }, [workbookModeEnabled]);

    useEffect(() => {
        const equipmentTypeIds = selectedEquipmentTypes.map(item => item.id);
        const systemIds = selectedSystems.map(item => item.id);

        const init = async () => {
            let promises = [];
            promises.push(fetchSiteEquipmentScores(activeNavSite.id, systemIds, equipmentTypeIds, { startDate: dashboardStartTimeStamp, endDate: dashboardEndTimeStamp }));
            if (selectedEquipment && selectedEquipment.id) {
                promises.push(fetchRuleScoreKpis(activeNavCompany.id, selectedEquipment.id, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Equipment' }));
            }
            await Promise.all(promises).then((results) => {
                setFilteredEquipment(results[0]);
                if (results.length === 2 && isArray(results[1])) {
                    setRuleScoreKpis(results[1]);
                }
            }).catch(err => err);
            
        };

        if (selectedEquipmentTypes || selectedSystems) {
            init();
        } else {
            setFilteredEquipment([]);
        }
    }, [selectedEquipmentTypes, selectedSystems, dashboardStartTimeStamp, dashboardEndTimeStamp]);

    const handleSelectEquipment = async rowData => {
        if (selectedEquipment && selectedEquipment.id === rowData.id) {
            return;
        }

        const currentRule = { ...selectedEquipmentRule };

        setRuleScoreKpis([]);
        setSelectedEquipment(rowData);
        setSelectedWeatherSensors([]);
        setEntityScoringData([]);
        setSelectedRuleSensors([]);
        setSelectedEquipmentRule({});

        let promises = [];
        if (view !== 'rawdata') {
            promises.push(fetchEntitySensors(activeNavSite.id, rowData.id));
        }
        promises.push(fetchRuleScoreKpis(activeNavCompany.id, rowData.id, { From: dashboardStartTimeStamp, To: dashboardEndTimeStamp, DesiredTimeSeriesIntervalInMinutes: desiredInterval, EntityName: 'Equipment' }));
        
        await Promise.all(promises.map(p => p.catch(err => err)));

        if (ruleScoreKpis.some(item => item.ruleId === currentRule.ruleId)) {
            setSelectedEquipmentRule(currentRule);
            if (view !== 'rawdata') {
                const ids = await fetchSensorIds(activeNavSite.id, rowData.id, [currentRule.ruleId]);
                setSelectedRuleSensors(ids);
                await fetchScoringData(activeNavCompany.id, ids, [currentRule.ruleId], dashboardStartTimeStamp, dashboardEndTimeStamp, desiredInterval, {EquipmentId: rowData.id});
            }
        }
    };

    const nameCell = props => {
        const {dataItem} = props;
        return (
            <td className={selectedEquipment.id === props.dataItem.id ? classes.SelectedNameCell : classes.NameCell} role="none">
                <Link
                    to={`/v2/dashboard/equipment?client=${activeNavClient.id}&company=${activeNavCompany.id}&equipment=${dataItem.id}&site=${activeNavSite.id}&viewChildId=4b25aeb8-45d4-4522-9f77-870ab40abb07`}
                    target={'_blank'}
                >
                    {props.dataItem[props.field]}
                </Link>
                <br />{props.dataItem.equipmentType ? props.dataItem.equipmentType.name : null}
            </td>
        );
    };

    const scoreCell = props => {
        let value = props.dataItem.score;
        value = value.toString().includes('.') ? parseFloat(value).toFixed(2) : value;

        const colorClass = value === null ? classes.NoColorvalue : value > 74 ? classes.Green : value > 49 ? classes.Yellow : value >= 0 ? classes.Red : classes.NoColor;
        const selected = props.dataItem.id === selectedEquipment.id ? classes.SelectedItem : classes.NameCell;

        return (
            <td className={clsx(props.className, colorClass, selected)} style={props.style}>
                {(value !== null && value !== '' && value !== undefined) ? value : null}
            </td>
        );
    };

    const onRowClick = e => {
        handleSelectEquipment(e.dataItem);
    };

    const onDataStateChange = e => {
        setGridDataState(e.dataState);
    };

    const clearGridFilter = filter => {
        setGridDataState({...gridDataState, filter: filter ? filter : initialDataStateFilter});
    };

    return (
        <React.Fragment>
            <style>
                { kendoGridInlineStyling }
                {
                    `.k-toolbar {
                        order: 1;
                        border-top: inset 1px rgba(0, 0, 0, 0.12);
                        max-height: 5.8rem;
                    }
                    `
                }
            </style>
            <div className={classes.Table}>
                <Grid
                    sortable={{
                        mode: 'multiple'
                    }}
                    className={classes.KendoGrid}
                    data={kendoProcess(filteredEquipment ? filteredEquipment : [], gridDataState)}
                    {...gridDataState}
                    onDataStateChange={onDataStateChange}
                    filterable={workbookModeEnabled}
                    onRowClick={onRowClick}
                >
                    {
                        workbookModeEnabled ?
                            <GridToolbar>
                                <FilterPanel
                                    title={'Rule Filter Builder'}
                                    filter={gridDataState.filter}
                                    filterText={gridFilterText}
                                    fields={gridFields}
                                    columns={[{name: 'Equipment', field: 'name', filter: 'text'}, {name: 'Score', field: 'score', filter: 'numeric'}]}
                                    setFilter={clearGridFilter}
                                    setFilterText={setGridFilterText}
                                />
                            </GridToolbar>
                            : null
                    }
                    <Column
                        field="name"
                        title="Equipment"
                        filter={'text'}
                        cell={nameCell}
                        width="200px"
                    />
                    <Column
                        title="Score"
                        field="score"
                        width="160px"
                        cell={scoreCell}
                        filter={'numeric'}
                    />
                </Grid>
                {
                    bobLoading.includes('EquipmentScores') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    activeNavClient:         state.bob.activeNavClient,
    activeNavCompany:        state.bob.activeNavCompany,
    activeNavSite:           state.bob.activeNavSite,
    bobLoading:              state.bob.bobLoading,
    dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp ? state.dashboard.dashboardEndTimeStamp.format() : null,
    dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp ? state.dashboard.dashboardStartTimeStamp.format() : null,
    desiredInterval:         state.dashboard.desiredInterval,
    navEquipment:            state.bob.navEquipment,
    ruleScoreKpis:           state.dashboard.ruleScoreKpis,
    workbookModeEnabled:     state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentScoreGrid);
